import { FC, ReactNode, useCallback, useMemo, useRef, useEffect, useReducer, ReactElement } from 'react';
import { Button, Flex, Form, Input, Modal, RadioChangeEvent, Tabs, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';

import {
  Table,
  TableProps,
  TableColumns,
  TableComponent,
  notification,
  TableColumn,
  CrudMode,
  TableActiveCell,
  TableCellValue,
  TableEditableCells,
  TableEditableColumns,
  getTableRowsErrorsCount,
  getTableEditableColumns,
  getTableFocusSequence,
  getTableColumnsErrorsCount,
  TableRowBase,
  transformToTableColumns,
} from '@ohpen/enso-ui';
import { ActionEntityType, ActionStatus, changeRequestGet, OperationType } from '@pmp/api';
import { useFeatureToggle } from '@pmp/hooks';
import { changeRequestButtonMessage } from '@pmp/utils';
import {
  TradingAddressesCR,
  OperationsWhitelistCR,
  ContactDetailsCR,
  LenderFirmCRDataRow,
  contactDetailsCRColumns,
  firmPermissionCRColumns,
  intermediaryPermissionCRColumns,
  firmPermissionColumnKeys,
  intermediaryPermissionColumnKeys,
  LENDER_COMPLIANCE_PERMISSION_LEVEL_LIST,
  buildLenderPermissionLevelValueList,
  tradingAddressCRColumns,
  buildTradingAddressRows,
  operationsWhitelistCRColumns,
  buildOperationsWhitelistRows,
  sortOperationsByType,
  AssistantsCR,
  AdvisersCR,
  assistantsCRColumns,
  advisersCRColumns,
  buildAssistantRows,
  buildAdviserRows,
  useTradingAddressesInCR,
  useQualifiedDomainsInCR,
  buildAssistantsOriginalDataObject,
  buildAdvisersOriginalDataObject,
  AssistantRow,
  AdviserRow,
} from '@pmp/components';
import { FieldType, IntermediaryDraftDataRow, UserRoleType } from '@pmp/api';

import styles from '../styles.module.css';

import {
  getEditableCells,
  getTitle,
  onApprove,
  onBulkAdjust,
  onCloneRow,
  onDeleteRow,
  onGoNext,
  onGoPrev,
  onOpen,
  onReject,
  onRequestChanges,
  onResendForApproval,
  onSave,
  onSendForApproval,
  rateChangeColumnKeys,
  changeCloneType,
  expandLenderFirmRowFeedback,
  expandIntermediaryRowFeedback,
  expandLenderRowFeedback,
  buildSubTableEditableCells,
  buildTradingAddressOriginalDataObject,
  buildOperationsWhitelistOriginalDataObject,
  buildOriginalDataObject,
  buildSubTableDataSource,
} from '../utils';
import { CrudTableProps } from '../interfaces';
import { getInitialState, reducer } from '../reducers';
import { ActionBar } from './ActionBar';
import { Alerts } from './Alerts';
import { getComponents, getFormattedColumns } from './CrudTableHelpers';

const { TextArea } = Input;

export const CrudTable = <EntityData, EntityCrudData extends { rowNumber: number }, EntityChangeDataRow>({
  entityType,
  actionId,
  columns,
  data,
  originalData = null,
  changeRequestData,
  validationFeedback = null,
  reviewInfo = null,
  mode,
  valueLists,
  additionalFields,
  isLoading,
  refreshTable,
  updateTable,
  resetSelectedRows,
  onClose,
}: CrudTableProps<EntityData, EntityCrudData>): ReactElement => {
  const tableRef = useRef<TableComponent | null>(null);
  const [state, dispatch] = useReducer(reducer<EntityCrudData>, getInitialState<EntityCrudData>());
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isView = mode === CrudMode.VIEW;
  const isReview = mode === CrudMode.REVIEW;
  const isViewResult = mode === CrudMode.VIEW_RESULT;
  const isEdit = mode === CrudMode.EDIT || mode === CrudMode.BULK_EDIT;
  const isClone = mode === CrudMode.CLONE || mode === CrudMode.BULK_CLONE;
  const isCreate = mode === CrudMode.CREATE;
  const isCrud = isEdit || isClone || isCreate;
  const isUpdateChange = mode === CrudMode.UPDATE_CHANGE;
  const isUpdateImportChange = mode === CrudMode.UPDATE_IMPORT_CHANGE;
  const enableEditingLiveProducts = useFeatureToggle(['enableEditingLiveProducts']);
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);
  const actionType = changeRequestData?.actionType.key as CrudMode | null;
  const isEditActionUpdate = isUpdateChange && (actionType === CrudMode.EDIT || actionType === CrudMode.BULK_EDIT);
  const status = changeRequestData?.status.key as ActionStatus | null;
  const invalidationSource = state.invalidationSource ?? changeRequestData?.invalidationSource ?? null;
  const isInvalidated = status === ActionStatus.INVALIDATED || !!state.invalidationSource;
  const hideShowIds = entityType && [ActionEntityType.ERC_PROFILES].includes(entityType);

  const additionalFieldsKeys = useMemo(() => Object.keys(additionalFields ?? {}), [additionalFields]);

  const handleOnClose = useCallback(() => {
    dispatch({ type: 'close' });
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (isLoading) return;
    onOpen({
      actionId,
      additionalFieldsKeys,
      data,
      dispatch,
      entityType,
      mode,
      notification,
      handleOnClose,
      originalData,
      reviewInfo,
      t,
      validationFeedback,
    });
  }, [entityType, actionId, additionalFieldsKeys, reviewInfo, data, originalData, mode, handleOnClose, t, isLoading, validationFeedback]);

  const canBeInvalidatedExternally = isReview || isUpdateChange || isUpdateImportChange;

  useEffect(() => {
    if (!state.isOpen || !state.actionId || !canBeInvalidatedExternally) return undefined;

    let cancelled = false;
    const pollActionStatus = () => {
      changeRequestGet({ path: { actionId: state.actionId! } })
        .then(({ data: latest }) => {
          if (!cancelled && latest.status.key === ActionStatus.INVALIDATED) {
            dispatch({ type: 'setInvalidationSource', invalidationSource: latest.invalidationSource ?? null });
          }
        })
        .catch(() => {});
    };

    pollActionStatus();
    const interval = setInterval(pollActionStatus, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [state.isOpen, state.actionId, canBeInvalidatedExternally]);

  const handleSendForApproval = useCallback(
    () =>
      onSendForApproval({
        actionId: state.actionId,
        cloneType: state.cloneType,
        dispatch,
        mode,
        notification,
        handleOnClose,
        refreshTable,
        resetSelectedRows,
        t,
        enableFourEyes,
      }),
    [handleOnClose, mode, refreshTable, resetSelectedRows, state.actionId, state.cloneType, t, enableFourEyes],
  );

  const handleResendForApproval = useCallback(
    () =>
      onResendForApproval({
        actionId: state.actionId,
        dispatch,
        notification,
        handleOnClose,
        t,
        resetSelectedRows,
        updateTable,
        valueLists,
      }),
    [handleOnClose, resetSelectedRows, state.actionId, t, dispatch, updateTable, valueLists],
  );

  const handleApprove = useCallback(
    () =>
      onApprove({
        actionId: state.actionId,
        dispatch,
        notification,
        handleOnClose,
        t,
        updateTable,
        valueLists,
      }),
    [handleOnClose, state.actionId, t, updateTable, valueLists],
  );

  const handleReject = useCallback(
    () =>
      onReject({
        actionId: state.actionId,
        dispatch,
        form,
        notification,
        handleOnClose,
        t,
        updateTable,
        valueLists,
      }),
    [form, handleOnClose, state.actionId, t, updateTable, valueLists],
  );

  const handleRequestChanges = useCallback(
    () =>
      onRequestChanges({
        actionId: state.actionId,
        dispatch,
        form,
        notification,
        handleOnClose,
        t,
        updateTable,
        valueLists,
      }),
    [form, handleOnClose, state.actionId, t, updateTable, valueLists],
  );

  const allColumns: TableColumns<EntityData> = useMemo(() => {
    const safeColumns = columns ?? [];
    const actionsColumn = safeColumns.find(column => column.key === 'actions') ?? { key: 'actions', title: 'global.labels.actions' };
    const crudColumns = safeColumns.filter(
      column =>
        !column.options?.hideInForm &&
        column.key !== 'actions'
          &&
        !(isCrud && column.hideInCrud),
    );
    const additionalColumns = additionalFieldsKeys ? transformToTableColumns<EntityData>(additionalFields) : [];
    const withActions = isCreate && entityType === ActionEntityType.PRODUCTS;

    return withActions ? [...crudColumns, ...additionalColumns, actionsColumn] : [...crudColumns, ...additionalColumns];
  }, [additionalFields, additionalFieldsKeys, columns, entityType, isCreate, isEdit]);

  const columnKeys: string[] = useMemo(
    () => Array.from(new Set(allColumns?.map(column => column.dataIndex).filter(key => typeof key === 'string') as string[])),
    [allColumns],
  );
  const editableCells: TableEditableCells = useMemo(
    () =>
      isInvalidated
        ? {}
        : getEditableCells({
            entityType,
            actionType,
            mode,
            columnKeys,
            originalDataSource: state.originalDataSource,
            dataSource: state.dataSource,
            isImport: isUpdateImportChange,
            allEditable: !isUpdateImportChange && !isEdit && !isEditActionUpdate,
            enableEditingLiveProducts,
          }),
    [
      entityType,
      actionType,
      mode,
      columnKeys,
      state.originalDataSource,
      state.dataSource,
      isUpdateImportChange,
      isEdit,
      isEditActionUpdate,
      enableEditingLiveProducts,
      isInvalidated,
    ],
  );

  const focusSequence: TableActiveCell[] = useMemo(() => getTableFocusSequence(editableCells), [editableCells]);

  const editableColumns: TableEditableColumns = useMemo(() => getTableEditableColumns(editableCells), [editableCells]);

  const rowsErrorsCount = useMemo(() => getTableRowsErrorsCount(state.validationFeedback), [state.validationFeedback]);

  const columnsErrorsCount = useMemo(() => getTableColumnsErrorsCount(state.validationFeedback), [state.validationFeedback]);

  const originalDataObject = useMemo(() => buildOriginalDataObject(state.originalDataSource), [state.originalDataSource]);

  const getNumberOfRows = useCallback(() => state.dataSource?.length ?? 0, [state.dataSource]);

  const handleDeleteRow = useCallback(
    (rowNumber: number) =>
      onDeleteRow({
        entityType,
        actionId: state.actionId,
        dataSource: state.dataSource,
        rowNumber,
        dispatch,
        notification,
        t,
      }),
    [entityType, state.actionId, state.dataSource, t],
  );

  const handleCloneRow = useCallback(
    (record: EntityCrudData) =>
      onCloneRow({
        entityType,
        actionId: state.actionId,
        additionalFieldsKeys,
        dataSource: state.dataSource,
        record,
        columnsToClean: [], // Ajusta si tienes columnas a limpiar
        notification,
        t,
        dispatch,
      }),
    [additionalFieldsKeys, state.actionId, state.dataSource, t, dispatch, entityType],
  );

  const handleChangeEditAll = useCallback((columnKey: string) => {
    dispatch({ type: 'toggleColumnEditAll', columnKey });
  }, []);

  const handleBulkAdjust = useCallback(
    (dataIndex: string, changes: { row: EntityCrudData; newValue: number }[]) =>
      onBulkAdjust({
        actionId: state.actionId,
        additionalFieldsKeys,
        dataIndex,
        dataSource: state.dataSource,
        dispatch,
        entityType,
        notification,
        t,
        changes,
      }),
    [state.actionId, state.dataSource, additionalFieldsKeys, entityType, t],
  );

  const setActiveCell = useCallback((activeCell: TableActiveCell) => {
    dispatch({ type: 'setActiveCell', activeCell });
  }, []);

  const goToNext = useCallback(
    () => onGoNext({ focusSequence, activeCell: state.activeCell, dispatch }),
    [focusSequence, state.activeCell, dispatch],
  );

  const goToPrev = useCallback(
    () => onGoPrev({ focusSequence, activeCell: state.activeCell, dispatch }),
    [focusSequence, state.activeCell, dispatch],
  );

  const onChangeCloneType = useCallback(
    (e: RadioChangeEvent) => {
      onSave({
        mode,
        actionId: state.actionId,
        cloneType: e.target.value,
        additionalFieldsKeys,
        dataIndex: 'originalCode',
        dataSource: state.dataSource,
        disableCancel: state.disableCancel,
        dispatch,
        entityType,
        goToNext,
        notification,
        handleOnClose,
        rowNumbers: [],
        skipNext: true,
        t,
        value: null,
      });
      changeCloneType(state.actionId, notification);
      dispatch({ type: 'setCloneType', cloneType: e.target.value });
      if (e.target.value === 'new' && state.view === 'rateChange') dispatch({ type: 'setView', view: 'all' });
    },
    [mode, state.actionId, state.dataSource, state.disableCancel, state.view, additionalFieldsKeys, entityType, goToNext, handleOnClose, t],
  );

  const { valueLists: qualifiedDomainsValueLists, rememberDomains } = useQualifiedDomainsInCR(entityType, state.dataSource);

  const handleSave = useCallback(
    (
      rowNumber: number,
      rowNumbers: number[],
      dataIndex: string,
      value: TableCellValue<EntityChangeDataRow>,
      skipNext: boolean,
    ): Promise<void> => {
      if (dataIndex === 'qualifiedDomains') rememberDomains(value);
      return onSave({
        mode,
        actionId: state.actionId,
        additionalFieldsKeys,
        cloneType: null,
        dataIndex,
        dataSource: state.dataSource,
        disableCancel: state.disableCancel,
        dispatch,
        entityType,
        goToNext,
        notification,
        handleOnClose,
        rowNumbers,
        skipNext,
        t,
        value,
      });
    },
    [
      mode,
      state.actionId,
      state.dataSource,
      state.disableCancel,
      additionalFieldsKeys,
      entityType,
      goToNext,
      notification,
      handleOnClose,
      rememberDomains,
      t,
    ],
  );

  const getRowActions: (record: EntityCrudData) => ReactElement[] = useCallback(
    record => {
      const rowActions = [
        <Tooltip key="delete" title={t('global.labels.delete')} placement="top">
          <Button
            type="text"
            aria-label={t('global.labels.deleteRow')}
            icon={<DeleteOutlined key="delete" />}
            onClick={() => handleDeleteRow(record.rowNumber)}
            disabled={state.dataSource?.length === 1}
          />
        </Tooltip>,
        <Tooltip key="clone" title={t('global.buttons.clone')} placement="top">
          <Button aria-label={t('global.labels.cloneRow')} type="text" icon={<CopyOutlined />} onClick={() => handleCloneRow(record)} />
        </Tooltip>,
      ];
      return rowActions;
    },
    [handleCloneRow, handleDeleteRow, state.dataSource?.length, t],
  );

  const getRowFeedback = useCallback((rowNumber: number) => state.validationFeedback?.[rowNumber], [state.validationFeedback]);

  const isLenderFirms = entityType === ActionEntityType.LENDER_FIRMS;
  const isBrokers = entityType === ActionEntityType.BROKERS;
  const isLenders = entityType === ActionEntityType.LENDERS;

  const {
    resolveTradingAddressList,
    valueLists: intermediaryValueLists,
    isLoading: isLoadingTradingAddresses,
  } = useTradingAddressesInCR(entityType, state.dataSource);
  const lenderPermissionLevelValueList = useMemo(
    () => ({ [LENDER_COMPLIANCE_PERMISSION_LEVEL_LIST]: buildLenderPermissionLevelValueList(t) }),
    [t],
  );

  const effectiveValueLists = useMemo(
    () => ({ ...valueLists, ...intermediaryValueLists, ...qualifiedDomainsValueLists, ...lenderPermissionLevelValueList }),
    [valueLists, intermediaryValueLists, qualifiedDomainsValueLists, lenderPermissionLevelValueList],
  );

  const getActiveColumnKey = useCallback(
    (rowNumber: number) => (state.activeCell?.rowNumber === rowNumber ? state.activeCell?.columnKey : null),
    [state.activeCell],
  );

  const reviewButtons = useMemo(
    () => (
      <>
        <Button type="primary" danger onClick={handleReject} disabled={state.isLoading || isInvalidated}>
          {t('global.buttons.reject')}
        </Button>
        <Button color="blue" variant="solid" onClick={handleRequestChanges} disabled={state.isLoading || isInvalidated}>
          {t('global.buttons.requestChanges')}
        </Button>
        <Button type="primary" onClick={handleApprove} disabled={state.isLoading || isInvalidated}>
          {t('global.buttons.approve')}
        </Button>
      </>
    ),
    [handleApprove, handleReject, handleRequestChanges, t, state.isLoading, isInvalidated],
  );

  const footer = useCallback(
    (originNode: ReactNode, { OkBtn, CancelBtn }: { OkBtn: FC; CancelBtn: FC }) => (
      <>
        <CancelBtn />
        {isReview && reviewButtons}
        {isCrud && (
          <Button
            type="primary"
            onClick={handleSendForApproval}
            disabled={!state.isValid}
          >
            {changeRequestButtonMessage(t, enableFourEyes)}
          </Button>
        )}
        {(isUpdateChange || isUpdateImportChange) && (
          <Button
            color="primary"
            variant="outlined"
            onClick={handleResendForApproval}
            disabled={!state.isValid}
          >
            {t('global.buttons.updateChanges')}
          </Button>
        )}
      </>
    ),
    [
      handleResendForApproval,
      handleSendForApproval,
      isCrud,
      isReview,
      isUpdateChange,
      isUpdateImportChange,
      reviewButtons,
      state.isValid,
      t,
      enableFourEyes,
    ],
  );

  const formattedColumns: TableColumns<EntityCrudData> = useMemo(
    () =>
      getFormattedColumns({
        allColumns,
        columnsEditAll: state.columnsEditAll,
        columnsErrorsCount,
        dataSource: state.dataSource,
        editableCells,
        getNumberOfRows,
        goToNext,
        goToPrev,
        handleBulkAdjust,
        handleChangeEditAll,
        handleSave,
        isClone,
        isCreate,
        isEdit,
        isUpdateChange,
        isUpdateImportChange,
        originalDataObject,
        setActiveCell,
        showIds: state.showIds,
        validationFeedback: state.validationFeedback,
        valueLists: effectiveValueLists,
        resolveCellValueList: resolveTradingAddressList,
      }),
    [
      allColumns,
      columnsErrorsCount,
      editableCells,
      getNumberOfRows,
      goToNext,
      goToPrev,
      handleBulkAdjust,
      handleChangeEditAll,
      handleSave,
      isClone,
      isCreate,
      isEdit,
      isUpdateChange,
      isUpdateImportChange,
      originalDataObject,
      setActiveCell,
      state.columnsEditAll,
      state.dataSource,
      state.showIds,
      state.validationFeedback,
      effectiveValueLists,
      resolveTradingAddressList,
    ],
  );

  const visibleColumns: string[] | undefined = useMemo(() => {
    const columnsKeys = formattedColumns?.map((column: TableColumn<EntityCrudData>) => column.key as string);
    const columnsByKey = Object.fromEntries(formattedColumns.map((col: TableColumn<EntityCrudData>) => [col.key, col]));
    if (state.view === 'columns' || state.view === 'cells' || state.view === 'editable') {
      return columnsKeys.filter(key => {
        if (key === 'code' || key === 'originalCode') return true;
        if (state.view === 'editable') return editableColumns[key];

        const dataIndex = columnsByKey[key]?.dataIndex as string;
        return !!columnsErrorsCount[key] || (dataIndex && dataIndex !== key && !!columnsErrorsCount[dataIndex]);
      });
    }
    if (state.view === 'rateChange') return columnsKeys?.filter(key => rateChangeColumnKeys.includes(key));
    return columnsKeys;
  }, [formattedColumns, columnsErrorsCount, editableColumns, state.view]);

  const filteredDataSource: TableRowBase<EntityCrudData>[] = useMemo(
    () =>
      state.view === 'all' ||
      state.view === 'columns' ||
      state.view === 'editable' ||
      state.view === 'rateChange' ||
      state.dataSource === null
        ? (state.dataSource ?? ([] as TableRowBase<EntityCrudData>[]))
        : state.dataSource.filter(row => rowsErrorsCount[row.rowNumber]),
    [rowsErrorsCount, state.dataSource, state.view],
  );

  const dataSource: TableRowBase<EntityCrudData>[] = useMemo(
    () =>
      filteredDataSource.map(row => {
        const originalRow = state.originalDataSource?.find(r => r.rowNumber === row.rowNumber) as Record<string, unknown> | undefined;
        const feedback = getRowFeedback(row.rowNumber);
        const flatRow = row as unknown as Record<string, unknown>;
        let rowFeedback = feedback;
        if (isLenderFirms) rowFeedback = expandLenderFirmRowFeedback(feedback, flatRow, originalRow);
        if (isBrokers) rowFeedback = expandIntermediaryRowFeedback(feedback, flatRow, originalRow);
        if (isLenders) rowFeedback = expandLenderRowFeedback(feedback, flatRow, originalRow);
        return ({
          ...row,
          activeColumnKey: getActiveColumnKey(row.rowNumber),
          rowFeedback,
        }) as TableRowBase<EntityCrudData>;
      }),
    [filteredDataSource, getActiveColumnKey, getRowFeedback, isBrokers, isLenderFirms, isLenders, state.originalDataSource],
  );

  const disabledCloneType = useMemo(() => dataSource?.some(record => !!record.lockedByActionId) ?? false, [dataSource]);

  const showIdOptions = useMemo(
    () => [
      { label: t('global.labels.showLabels'), value: false },
      { label: t('global.labels.showIds'), value: true },
    ],
    [t],
  );

  const virtual = useMemo(() => dataSource.length > 100, [dataSource.length]);

  const components = useMemo(() => getComponents(virtual), [virtual]);

  // --- Base table props (no expandable) ---
  const tableProps: TableProps<ActionEntityType> = useMemo(
    () => ({
      id: 'products-crud',
      ref: tableRef,
      dataSource,
      columns: formattedColumns,
      rowKey: 'rowNumber',
      pagination: false,
      virtual,
      scroll: { x: 'max-content', y: virtual ? 400 : 570 },
      loading: state.isLoading || isLoading || isLoadingTradingAddresses,
      valueLists: effectiveValueLists,
      components,
      className: isView || isReview || isViewResult || isInvalidated ? 'view-table' : 'editable-table',
      popupContainerClassName: 'ant-modal-body',
      rowClassName: () => 'editable-row',
      visibleColumns,
      skeletonLoadingProps: { loading: state.validationFeedback === null },
      getRowActions,
    }),
    [
      dataSource,
      formattedColumns,
      virtual,
      state.isLoading,
      state.validationFeedback,
      isLoading,
      isLoadingTradingAddresses,
      effectiveValueLists,
      components,
      isView,
      isReview,
      isViewResult,
      isInvalidated,
      visibleColumns,
      getRowActions,
    ],
  );

  const contactDetailsColumnKeys = useMemo(
    () => contactDetailsCRColumns.map(c => c.dataIndex).filter((k): k is string => typeof k === 'string'),
    [],
  );

  const contactDetailsEditableCells: TableEditableCells = useMemo(
    () =>
      getEditableCells({
        entityType,
        actionType,
        mode,
        columnKeys: contactDetailsColumnKeys,
        originalDataSource: state.originalDataSource,
        dataSource: state.dataSource,
        isImport: isUpdateImportChange,
        allEditable: !isUpdateImportChange && !isEdit && !isEditActionUpdate,
        enableEditingLiveProducts,
      }),
    [
      entityType,
      actionType,
      mode,
      contactDetailsColumnKeys,
      state.originalDataSource,
      state.dataSource,
      isUpdateImportChange,
      isEdit,
      isEditActionUpdate,
      enableEditingLiveProducts,
    ],
  );

  const formattedContactDetailsColumns = useMemo(
    () =>
      getFormattedColumns({
        allColumns: contactDetailsCRColumns,
        columnsEditAll: state.columnsEditAll,
        columnsErrorsCount,
        dataSource: state.dataSource,
        editableCells: contactDetailsEditableCells,
        getNumberOfRows,
        goToNext,
        goToPrev,
        handleBulkAdjust,
        handleChangeEditAll,
        handleSave,
        isClone,
        isCreate,
        isEdit,
        isUpdateChange,
        isUpdateImportChange,
        originalDataObject,
        setActiveCell,
        showIds: state.showIds,
        validationFeedback: state.validationFeedback,
        valueLists: effectiveValueLists,
      }),
    [
      columnsErrorsCount,
      contactDetailsEditableCells,
      getNumberOfRows,
      goToNext,
      goToPrev,
      handleBulkAdjust,
      handleChangeEditAll,
      handleSave,
      isClone,
      isCreate,
      isEdit,
      isUpdateChange,
      isUpdateImportChange,
      originalDataObject,
      setActiveCell,
      state.columnsEditAll,
      state.dataSource,
      state.showIds,
      state.validationFeedback,
      effectiveValueLists,
    ],
  );

  const contactDetailsTab = useMemo(() => {
    if (!isLenderFirms || !state.dataSource) return null;
    return (
      <ContactDetailsCR
        tableProps={{
          ...tableProps,
          id: 'contact-details-crud',
          columns: formattedContactDetailsColumns,
          visibleColumns: undefined,
        }}
      />
    );
  }, [isLenderFirms, state.dataSource, tableProps, formattedContactDetailsColumns]);

  const firmPermissionEditableCells: TableEditableCells = useMemo(
    () =>
      getEditableCells({
        entityType,
        actionType,
        mode,
        columnKeys: firmPermissionColumnKeys,
        originalDataSource: state.originalDataSource,
        dataSource: state.dataSource,
        isImport: isUpdateImportChange,
        allEditable: !isUpdateImportChange && !isEdit && !isEditActionUpdate,
        enableEditingLiveProducts,
      }),
    [
      entityType,
      actionType,
      mode,
      firmPermissionColumnKeys,
      state.originalDataSource,
      state.dataSource,
      isUpdateImportChange,
      isEdit,
      isEditActionUpdate,
      enableEditingLiveProducts,
    ],
  );

  const formattedFirmPermissionColumns = useMemo(
    () =>
      getFormattedColumns({
        allColumns: firmPermissionCRColumns,
        columnsEditAll: state.columnsEditAll,
        columnsErrorsCount,
        dataSource: state.dataSource,
        editableCells: firmPermissionEditableCells,
        getNumberOfRows,
        goToNext,
        goToPrev,
        handleBulkAdjust,
        handleChangeEditAll,
        handleSave,
        isClone,
        isCreate,
        isEdit,
        isUpdateChange,
        isUpdateImportChange,
        originalDataObject,
        setActiveCell,
        showIds: state.showIds,
        validationFeedback: state.validationFeedback,
        valueLists: effectiveValueLists,
      }),
    [
      columnsErrorsCount,
      firmPermissionEditableCells,
      getNumberOfRows,
      goToNext,
      goToPrev,
      handleBulkAdjust,
      handleChangeEditAll,
      handleSave,
      isClone,
      isCreate,
      isEdit,
      isUpdateChange,
      isUpdateImportChange,
      originalDataObject,
      setActiveCell,
      state.columnsEditAll,
      state.dataSource,
      state.showIds,
      state.validationFeedback,
      effectiveValueLists,
    ],
  );

  const firmPermissionsTab = useMemo(() => {
    if (!isLenders || !state.dataSource) return null;
    return (
      <ContactDetailsCR
        tableProps={{
          ...tableProps,
          id: 'firm-permissions-crud',
          columns: formattedFirmPermissionColumns,
          visibleColumns: undefined,
        }}
      />
    );
  }, [isLenders, state.dataSource, tableProps, formattedFirmPermissionColumns]);

  const intermediaryPermissionEditableCells: TableEditableCells = useMemo(
    () =>
      getEditableCells({
        entityType,
        actionType,
        mode,
        columnKeys: intermediaryPermissionColumnKeys,
        originalDataSource: state.originalDataSource,
        dataSource: state.dataSource,
        isImport: isUpdateImportChange,
        allEditable: !isUpdateImportChange && !isEdit && !isEditActionUpdate,
        enableEditingLiveProducts,
      }),
    [
      entityType,
      actionType,
      mode,
      intermediaryPermissionColumnKeys,
      state.originalDataSource,
      state.dataSource,
      isUpdateImportChange,
      isEdit,
      isEditActionUpdate,
      enableEditingLiveProducts,
    ],
  );

  const formattedIntermediaryPermissionColumns = useMemo(
    () =>
      getFormattedColumns({
        allColumns: intermediaryPermissionCRColumns,
        columnsEditAll: state.columnsEditAll,
        columnsErrorsCount,
        dataSource: state.dataSource,
        editableCells: intermediaryPermissionEditableCells,
        getNumberOfRows,
        goToNext,
        goToPrev,
        handleBulkAdjust,
        handleChangeEditAll,
        handleSave,
        isClone,
        isCreate,
        isEdit,
        isUpdateChange,
        isUpdateImportChange,
        originalDataObject,
        setActiveCell,
        showIds: state.showIds,
        validationFeedback: state.validationFeedback,
        valueLists: effectiveValueLists,
      }),
    [
      columnsErrorsCount,
      intermediaryPermissionEditableCells,
      getNumberOfRows,
      goToNext,
      goToPrev,
      handleBulkAdjust,
      handleChangeEditAll,
      handleSave,
      isClone,
      isCreate,
      isEdit,
      isUpdateChange,
      isUpdateImportChange,
      originalDataObject,
      setActiveCell,
      state.columnsEditAll,
      state.dataSource,
      state.showIds,
      state.validationFeedback,
      effectiveValueLists,
    ],
  );

  const intermediaryPermissionsTab = useMemo(() => {
    if (!isLenders || !state.dataSource) return null;
    return (
      <ContactDetailsCR
        tableProps={{
          ...tableProps,
          id: 'intermediary-permissions-crud',
          columns: formattedIntermediaryPermissionColumns,
          visibleColumns: undefined,
        }}
      />
    );
  }, [isLenders, state.dataSource, tableProps, formattedIntermediaryPermissionColumns]);

  const tradingAddressRows = useMemo(
    () => (isLenderFirms && state.dataSource ? buildTradingAddressRows(state.dataSource as unknown as LenderFirmCRDataRow[]) : []),
    [isLenderFirms, state.dataSource],
  );

  const tradingAddressColumnKeys = useMemo(
    () => tradingAddressCRColumns.map(c => c.dataIndex).filter((k): k is string => typeof k === 'string'),
    [],
  );

  const tradingAddressEditableCells: TableEditableCells = useMemo(() => {
    if (isInvalidated || (!isCrud && !isUpdateChange && !isUpdateImportChange)) return {};
    return buildSubTableEditableCells(tradingAddressRows, tradingAddressColumnKeys);
  }, [isCrud, isUpdateChange, isUpdateImportChange, tradingAddressRows, tradingAddressColumnKeys, isInvalidated]);

  const tradingAddressOriginalDataObject = useMemo(() => {
    const originalFirms = isLenderFirms && state.originalDataSource
      ? (state.originalDataSource as unknown as LenderFirmCRDataRow[])
      : [];
    return buildTradingAddressOriginalDataObject(tradingAddressRows, originalFirms);
  }, [isLenderFirms, tradingAddressRows, state.originalDataSource]);

  const handleTradingAddressSave = useCallback(
    (rowNumber: number, _rowNumbers: number[], dataIndex: string, value: unknown, skipNext: boolean): Promise<void> => {
      const tradingAddressRow = tradingAddressRows.find(r => r.rowNumber === rowNumber);
      if (!tradingAddressRow || !state.dataSource) return Promise.resolve();
      const firmRowNumber = tradingAddressRow.firmRowNumber;
      const addressIndexInFirm = tradingAddressRows.filter(
        r => r.firmRowNumber === firmRowNumber && r.rowNumber < rowNumber,
      ).length;
      const firmData = (state.dataSource as unknown as LenderFirmCRDataRow[]).find(f => f.rowNumber === firmRowNumber);
      if (!firmData?.tradingAddresses?.[addressIndexInFirm]) return Promise.resolve();
      const addresses = [...firmData.tradingAddresses];
      const lineItemKeys = ['line1', 'line2', 'line3', 'line4', 'line5'] as const;
      if (lineItemKeys.includes(dataIndex as typeof lineItemKeys[number])) {
        const existing = addresses[addressIndexInFirm];
        const updatedRow = { ...tradingAddressRow, [dataIndex]: value };
        const lineItems = lineItemKeys.map(k => updatedRow[k] ?? null).filter((v): v is string => v != null);
        addresses[addressIndexInFirm] = { ...existing, lineItems };
      } else {
        addresses[addressIndexInFirm] = { ...addresses[addressIndexInFirm], [dataIndex]: value };
      }
      return onSave({
        mode,
        actionId: state.actionId,
        additionalFieldsKeys,
        cloneType: null,
        dataIndex: 'tradingAddresses',
        dataSource: state.dataSource,
        disableCancel: state.disableCancel,
        dispatch,
        entityType,
        goToNext,
        notification,
        handleOnClose,
        rowNumbers: [firmRowNumber],
        skipNext,
        t,
        value: addresses,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tradingAddressRows, state.dataSource, state.actionId, state.disableCancel, additionalFieldsKeys, entityType, goToNext, handleOnClose, mode, t, dispatch],
  );

  const formattedTradingAddressColumns = useMemo(
    () =>
      getFormattedColumns({
        allColumns: tradingAddressCRColumns,
        columnsEditAll: {},
        columnsErrorsCount: {},
        editableCells: tradingAddressEditableCells,
        getNumberOfRows: () => tradingAddressRows.length,
        goToNext,
        goToPrev,
        handleChangeEditAll,
        handleSave: handleTradingAddressSave,
        isClone,
        isCreate,
        isEdit,
        isUpdateChange,
        isUpdateImportChange,
        originalDataObject: tradingAddressOriginalDataObject,
        setActiveCell,
        showIds: state.showIds,
        validationFeedback: null,
        valueLists,
      }),
    [
      tradingAddressEditableCells,
      tradingAddressRows.length,
      tradingAddressOriginalDataObject,
      goToNext,
      goToPrev,
      handleChangeEditAll,
      handleTradingAddressSave,
      isClone,
      isCreate,
      isEdit,
      isUpdateChange,
      isUpdateImportChange,
      setActiveCell,
      state.showIds,
      valueLists,
    ],
  );

  const tradingAddressDataSource = useMemo(
    () => buildSubTableDataSource(
      tradingAddressRows,
      tradingAddressColumnKeys,
      'tradingAddresses',
      state.validationFeedback,
      row => row.firmRowNumber,
      getActiveColumnKey,
      tradingAddressOriginalDataObject,
    ),
    [tradingAddressRows, tradingAddressColumnKeys, state.validationFeedback, getActiveColumnKey, tradingAddressOriginalDataObject],
  );

  const tradingAddressesTab = useMemo(() => {
    if (!isLenderFirms || !state.dataSource) return null;
    return (
      <TradingAddressesCR
        tableProps={{
          ...tableProps,
          id: 'trading-addresses-crud',
          dataSource: tradingAddressDataSource,
          columns: formattedTradingAddressColumns,
          visibleColumns: undefined,
        }}
      />
    );
  }, [isLenderFirms, state.dataSource, tableProps, tradingAddressDataSource, formattedTradingAddressColumns]);

  // --- Operations whitelist tab ---
  const operationsWhitelistRows = useMemo(
    () => (isLenderFirms && state.dataSource ? buildOperationsWhitelistRows(state.dataSource as unknown as LenderFirmCRDataRow[]) : []),
    [isLenderFirms, state.dataSource],
  );

  const operationsWhitelistColumnKeys = useMemo(
    () => operationsWhitelistCRColumns.map(c => c.dataIndex).filter((k): k is string => typeof k === 'string'),
    [],
  );

  const operationsWhitelistEditableCells: TableEditableCells = useMemo(() => {
    if (isInvalidated || (!isCrud && !isUpdateChange && !isUpdateImportChange)) return {};
    return buildSubTableEditableCells(operationsWhitelistRows, operationsWhitelistColumnKeys);
  }, [isCrud, isUpdateChange, isUpdateImportChange, operationsWhitelistRows, operationsWhitelistColumnKeys, isInvalidated]);

  const operationsWhitelistOriginalDataObject = useMemo(() => {
    const originalFirms = isLenderFirms && state.originalDataSource
      ? (state.originalDataSource as unknown as LenderFirmCRDataRow[])
      : [];
    return buildOperationsWhitelistOriginalDataObject(operationsWhitelistRows, originalFirms);
  }, [isLenderFirms, operationsWhitelistRows, state.originalDataSource]);

  const handleOperationsWhitelistSave = useCallback(
    (rowNumber: number, _rowNumbers: number[], dataIndex: string, value: unknown, skipNext: boolean): Promise<void> => {
      const opRow = operationsWhitelistRows.find(r => r.rowNumber === rowNumber);
      if (!opRow || !state.dataSource) return Promise.resolve();
      const firmRowNumber = opRow.firmRowNumber;
      const opIndexInFirm = operationsWhitelistRows.filter(
        r => r.firmRowNumber === firmRowNumber && r.rowNumber < rowNumber,
      ).length;
      const firmData = (state.dataSource as unknown as LenderFirmCRDataRow[]).find(f => f.rowNumber === firmRowNumber);
      // Rows are built via sortOperationsByType, so the positional index only matches a copy sorted the same way.
      const ops = sortOperationsByType(firmData?.operationsWhitelist ?? []);
      if (!ops[opIndexInFirm]) return Promise.resolve();
      const existingOp = ops[opIndexInFirm];
      if (dataIndex === 'type') {
        ops[opIndexInFirm] = { ...existingOp, type: value as OperationType };
      } else {
        ops[opIndexInFirm] = {
          ...existingOp,
          bankAccountDetails: { ...existingOp.bankAccountDetails, [dataIndex]: value },
        };
      }
      return onSave({
        mode,
        actionId: state.actionId,
        additionalFieldsKeys,
        cloneType: null,
        dataIndex: 'operationsWhitelist',
        dataSource: state.dataSource,
        disableCancel: state.disableCancel,
        dispatch,
        entityType,
        goToNext,
        notification,
        handleOnClose,
        rowNumbers: [firmRowNumber],
        skipNext,
        t,
        value: ops,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operationsWhitelistRows, state.dataSource, state.actionId, state.disableCancel, additionalFieldsKeys, entityType, goToNext, handleOnClose, mode, t, dispatch],
  );

  const formattedOperationsWhitelistColumns = useMemo(
    () =>
      getFormattedColumns({
        allColumns: operationsWhitelistCRColumns,
        columnsEditAll: {},
        columnsErrorsCount: {},
        editableCells: operationsWhitelistEditableCells,
        getNumberOfRows: () => operationsWhitelistRows.length,
        goToNext,
        goToPrev,
        handleChangeEditAll,
        handleSave: handleOperationsWhitelistSave,
        isClone,
        isCreate,
        isEdit,
        isUpdateChange,
        isUpdateImportChange,
        originalDataObject: operationsWhitelistOriginalDataObject,
        setActiveCell,
        showIds: state.showIds,
        validationFeedback: null,
        valueLists,
      }),
    [
      operationsWhitelistEditableCells,
      operationsWhitelistRows.length,
      operationsWhitelistOriginalDataObject,
      goToNext,
      goToPrev,
      handleChangeEditAll,
      handleOperationsWhitelistSave,
      isClone,
      isCreate,
      isEdit,
      isUpdateChange,
      isUpdateImportChange,
      setActiveCell,
      state.showIds,
      valueLists,
    ],
  );

  const operationsWhitelistDataSource = useMemo(
    () => buildSubTableDataSource(
      operationsWhitelistRows,
      operationsWhitelistColumnKeys,
      'operationsWhitelist',
      state.validationFeedback,
      row => row.firmRowNumber,
      getActiveColumnKey,
      operationsWhitelistOriginalDataObject,
    ),
    [operationsWhitelistRows, operationsWhitelistColumnKeys, state.validationFeedback, getActiveColumnKey, operationsWhitelistOriginalDataObject],
  );

  const operationsWhitelistTab = useMemo(() => {
    if (!isLenderFirms || !state.dataSource) return null;
    return (
      <OperationsWhitelistCR
        tableProps={{
          ...tableProps,
          id: 'operations-whitelist-crud',
          dataSource: operationsWhitelistDataSource,
          columns: formattedOperationsWhitelistColumns,
          visibleColumns: undefined,
        }}
      />
    );
  }, [isLenderFirms, state.dataSource, tableProps, operationsWhitelistDataSource, formattedOperationsWhitelistColumns]);

  // --- Assistants tab (brokers) ---
  const assistantRows = useMemo(
    () => (isBrokers && state.dataSource ? buildAssistantRows(state.dataSource as unknown as IntermediaryDraftDataRow[]) : []),
    [isBrokers, state.dataSource],
  );

  const assistantColumnKeys = useMemo(
    () => assistantsCRColumns.map(c => c.dataIndex).filter((k): k is string => typeof k === 'string'),
    [],
  );

  const assistantsOriginalDataObject = useMemo(() => {
    const originalBrokers = isBrokers && state.originalDataSource
      ? (state.originalDataSource as unknown as IntermediaryDraftDataRow[])
      : [];
    return buildAssistantsOriginalDataObject(assistantRows, originalBrokers, valueLists[FieldType.ASSISTANT_PERMISSION_TYPE] ?? {});
  }, [isBrokers, assistantRows, state.originalDataSource, valueLists]);

  const assistantsDataSource = useMemo(
    () => buildSubTableDataSource(
      assistantRows,
      assistantColumnKeys,
      'assistants',
      state.validationFeedback,
      row => row.brokerRowNumber,
      getActiveColumnKey,
      assistantsOriginalDataObject,
    ),
    [assistantRows, assistantColumnKeys, state.validationFeedback, getActiveColumnKey, assistantsOriginalDataObject],
  );

  const assistantsEditableCells: TableEditableCells = useMemo(() => {
    if (isInvalidated || (!isCrud && !isUpdateChange && !isUpdateImportChange)) return {};
    return buildSubTableEditableCells(assistantRows, ['permissionType']);
  }, [isCrud, isUpdateChange, isUpdateImportChange, assistantRows, isInvalidated]);

  const handleCollaborationSave = useCallback(
    (kind: 'assistants' | 'advisers', rows: Array<AssistantRow | AdviserRow>) =>
      (rowNumber: number, _rowNumbers: number[], dataIndex: string, value: unknown, skipNext: boolean): Promise<void> => {
        const idKey = kind === 'assistants' ? 'assistantId' : 'adviserId';
        const row = rows.find(r => r.rowNumber === rowNumber) as Record<string, unknown> | undefined;
        if (!row || !state.dataSource) return Promise.resolve();
        const brokerRowNumber = row.brokerRowNumber as number;
        const brokerData = (state.dataSource as unknown as IntermediaryDraftDataRow[]).find(b => (b.rowNumber ?? 0) === brokerRowNumber);
        const collection = brokerData?.[kind] as Array<Record<string, unknown>> | null | undefined;
        const index = (collection ?? []).findIndex(item => item[idKey] === row[idKey]);
        if (!collection || index === -1) return Promise.resolve();
        const fieldValue = value && typeof value === 'object' && 'key' in value ? (value as { key?: string | null }).key ?? null : value;
        const updated = [...collection];
        updated[index] = { ...updated[index], [dataIndex]: fieldValue };
        return onSave({
          mode,
          actionId: state.actionId,
          additionalFieldsKeys,
          cloneType: null,
          dataIndex: kind,
          dataSource: state.dataSource,
          disableCancel: state.disableCancel,
          dispatch,
          entityType,
          goToNext,
          notification,
          handleOnClose,
          rowNumbers: [brokerRowNumber],
          skipNext,
          t,
          value: updated,
        });
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.dataSource, state.actionId, state.disableCancel, additionalFieldsKeys, entityType, goToNext, handleOnClose, mode, t, dispatch],
  );

  const formattedAssistantsColumns = useMemo(
    () =>
      getFormattedColumns({
        allColumns: assistantsCRColumns,
        columnsEditAll: {},
        columnsErrorsCount: {},
        editableCells: assistantsEditableCells,
        getNumberOfRows: () => assistantRows.length,
        goToNext,
        goToPrev,
        handleChangeEditAll,
        handleSave: handleCollaborationSave('assistants', assistantRows),
        isClone,
        isCreate,
        isEdit,
        isUpdateChange,
        isUpdateImportChange,
        originalDataObject: assistantsOriginalDataObject,
        setActiveCell,
        showIds: state.showIds,
        validationFeedback: null,
        valueLists,
      }),
    [assistantsEditableCells, handleCollaborationSave, assistantRows, assistantsOriginalDataObject, goToNext, goToPrev, handleChangeEditAll, isClone, isCreate, isEdit, isUpdateChange, isUpdateImportChange, setActiveCell, state.showIds, valueLists],
  );

  const assistantsTab = useMemo(() => {
    if (!isBrokers || !state.dataSource) return null;
    return (
      <AssistantsCR
        tableProps={{
          ...tableProps,
          id: 'assistants-crud',
          rowKey: 'rowNumber',
          groupKey: undefined,
          expanded: undefined,
          dataSource: assistantsDataSource,
          columns: formattedAssistantsColumns,
          visibleColumns: undefined,
        }}
      />
    );
  }, [isBrokers, state.dataSource, tableProps, assistantsDataSource, formattedAssistantsColumns]);

  // --- Advisers tab (brokers) ---
  const adviserRows = useMemo(
    () => (isBrokers && state.dataSource ? buildAdviserRows(state.dataSource as unknown as IntermediaryDraftDataRow[]) : []),
    [isBrokers, state.dataSource],
  );

  const adviserColumnKeys = useMemo(
    () => advisersCRColumns.map(c => c.dataIndex).filter((k): k is string => typeof k === 'string'),
    [],
  );

  const advisersOriginalDataObject = useMemo(() => {
    const originalBrokers = isBrokers && state.originalDataSource
      ? (state.originalDataSource as unknown as IntermediaryDraftDataRow[])
      : [];
    return buildAdvisersOriginalDataObject(adviserRows, originalBrokers, valueLists[FieldType.ASSISTANT_PERMISSION_TYPE] ?? {});
  }, [isBrokers, adviserRows, state.originalDataSource, valueLists]);

  const advisersDataSource = useMemo(
    () => buildSubTableDataSource(
      adviserRows,
      adviserColumnKeys,
      'advisers',
      state.validationFeedback,
      row => row.brokerRowNumber,
      getActiveColumnKey,
      advisersOriginalDataObject,
    ),
    [adviserRows, adviserColumnKeys, state.validationFeedback, getActiveColumnKey, advisersOriginalDataObject],
  );

  const advisersEditableCells: TableEditableCells = useMemo(() => {
    if (isInvalidated || (!isCrud && !isUpdateChange && !isUpdateImportChange)) return {};
    return buildSubTableEditableCells(adviserRows, ['permissionType']);
  }, [isCrud, isUpdateChange, isUpdateImportChange, adviserRows, isInvalidated]);

  const formattedAdvisersColumns = useMemo(
    () =>
      getFormattedColumns({
        allColumns: advisersCRColumns,
        columnsEditAll: {},
        columnsErrorsCount: {},
        editableCells: advisersEditableCells,
        getNumberOfRows: () => adviserRows.length,
        goToNext,
        goToPrev,
        handleChangeEditAll,
        handleSave: handleCollaborationSave('advisers', adviserRows),
        isClone,
        isCreate,
        isEdit,
        isUpdateChange,
        isUpdateImportChange,
        originalDataObject: advisersOriginalDataObject,
        setActiveCell,
        showIds: state.showIds,
        validationFeedback: null,
        valueLists,
      }),
    [advisersEditableCells, handleCollaborationSave, adviserRows, advisersOriginalDataObject, goToNext, goToPrev, handleChangeEditAll, isClone, isCreate, isEdit, isUpdateChange, isUpdateImportChange, setActiveCell, state.showIds, valueLists],
  );

  const advisersTab = useMemo(() => {
    if (!isBrokers || !state.dataSource) return null;
    return (
      <AdvisersCR
        tableProps={{
          ...tableProps,
          id: 'advisers-crud',
          rowKey: 'rowNumber',
          groupKey: undefined,
          expanded: undefined,
          dataSource: advisersDataSource,
          columns: formattedAdvisersColumns,
          visibleColumns: undefined,
        }}
      />
    );
  }, [isBrokers, state.dataSource, tableProps, advisersDataSource, formattedAdvisersColumns]);

  const { isAdvisor, isAssistant } = useMemo(() => {
    const brokers = isBrokers && state.dataSource ? (state.dataSource as unknown as IntermediaryDraftDataRow[]) : [];
    return {
      isAdvisor: brokers.some(broker => broker.roles?.includes(UserRoleType.ADVISOR)),
      isAssistant: brokers.some(broker => broker.roles?.includes(UserRoleType.ASSISTANT)),
    };
  }, [isBrokers, state.dataSource]);

  const title = useMemo(() => getTitle(entityType, mode), [entityType, mode]);

  const okButtonProps = useMemo(
    () => ({ disabled: !state.isValid || isLoading || isInvalidated, loading: state.isLoading || state.isValidating }),
    [isLoading, state.isLoading, state.isValid, state.isValidating, isInvalidated],
  );

  const cancelButtonProps = useMemo(() => ({ disabled: state.disableCancel }), [state.disableCancel]);

  const actionBar = (
    <ActionBar
      actionType={actionType}
      isCrud={isCrud}
      isEdit={isEdit}
      isClone={isClone}
      isCreate={isCreate}
      isUpdateChange={isUpdateChange}
      hideShowIds={hideShowIds}
      isLoading={isLoading}
      state={state}
      changeRequestData={changeRequestData}
      disabledCloneType={disabledCloneType}
      showIdOptions={showIdOptions}
      rowsErrorsCount={rowsErrorsCount}
      columnsErrorsCount={columnsErrorsCount}
      editableColumns={editableColumns}
      valueLists={valueLists}
      onChangeCloneType={onChangeCloneType}
      dispatch={dispatch}
    />
  );

  let mainContent = <Table {...tableProps}>{actionBar}</Table>;
  if (isLenderFirms) {
    mainContent = (
      <Tabs
        className={styles.tabs}
        defaultActiveKey="firms"
        items={[
          {
            key: 'firms',
            label: t('lender.firms.labels.firmTitle') || 'Firm Details',
            children: <Table {...tableProps}>{actionBar}</Table>,
          },
          {
            key: 'contactDetails',
            label: t('lender.firms.labels.contactDetails') || 'Contact Details',
            children: contactDetailsTab,
          },
          {
            key: 'tradingAddresses',
            label: t('lender.firms.labels.tradingNames') || 'Trading Addresses',
            children: tradingAddressesTab,
          },
          {
            key: 'operationsWhitelist',
            label: t('lender.firms.labels.operationsWhitelist') || 'Operations Whitelist',
            children: operationsWhitelistTab,
          },
        ]}
      />
    );
  } else if (isBrokers) {
    mainContent = (
      <Tabs
        className={styles.tabs}
        defaultActiveKey="intermediaries"
        items={[
          {
            key: 'intermediaries',
            label: t('lender.brokers.labels.intermediary') || 'Broker Details',
            children: <Table {...tableProps}>{actionBar}</Table>,
          },
          ...(isAdvisor
            ? [
                {
                  key: 'assistants',
                  label: t('lender.brokers.labels.assistants') || 'Assistants',
                  children: assistantsTab,
                },
              ]
            : []),
          ...(isAssistant
            ? [
                {
                  key: 'advisers',
                  label: t('lender.brokers.labels.advisers') || 'Advisers',
                  children: advisersTab,
                },
              ]
            : []),
        ]}
      />
    );
  } else if (isLenders) {
    mainContent = (
      <Tabs
        className={styles.tabs}
        defaultActiveKey="lender"
        items={[
          {
            key: 'lender',
            label: t('lender.lender.title') || 'Lender',
            children: <Table {...tableProps}>{actionBar}</Table>,
          },
          {
            key: 'firmPermissions',
            label: t('lender.lender.labels.firmComplianceConfiguration') || 'Firm Permissions',
            children: firmPermissionsTab,
          },
          {
            key: 'intermediaryPermissions',
            label: t('lender.lender.labels.intermediaryComplianceConfiguration') || 'Intermediary Permissions',
            children: intermediaryPermissionsTab,
          },
        ]}
      />
    );
  }

  return (
    <Modal
      title={title}
      open={state.isOpen}
      footer={footer}
      onCancel={handleOnClose}
      cancelText={isView ? t('global.buttons.close') : undefined}
      okButtonProps={okButtonProps}
      cancelButtonProps={cancelButtonProps}
      destroyOnHidden={true}
      closable={!state.disableCancel}
      maskClosable={false}
      width={'95vw'}
      zIndex={1001}
      className={styles.crudTableModal}
    >
      <Flex vertical gap="0">
        <Alerts
          status={status}
          state={state}
          isUpdateChange={isUpdateChange}
          isUpdateImportChange={isUpdateImportChange}
          processingError={changeRequestData?.processingError}
          invalidationSource={invalidationSource}
        />
        <Flex vertical>
          {mainContent}
          {!isCrud && (
            <Form form={form} name="comment" autoComplete="off" layout="vertical">
              {state.reviewInfo?.reviewComment && (
                <TextArea rows={3} cols={150} value={state.reviewInfo.reviewComment} disabled={!isReview} />
              )}
              {isReview && (
                <Form.Item name="comment" rules={[{ required: true, message: t('global.errors.required') }]}>
                  <TextArea rows={3} cols={150} placeholder={isReview ? t('global.labels.changesRequestReason') : undefined} />
                </Form.Item>
              )}
            </Form>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};
